import dayjs from 'dayjs';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';

import { PopperMenu, PopperMenuItem } from 'pages/Shipments/Operation/AggregatorAging/AggregatorAging.styled';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

import * as S from './CompositeFilter.styled';

export const ANALYSIS_FILTERS = {
	shipmentCreatedDate: {
		label: 'shipmentCreatedDate',
		type: 'date',
		dateProps: { rangeMode: true },
		filterValues: {
			key: ['shipmentCreatedDate'],
			action: FilterAction.between,
			filterType: FilterType.date,
			from: dayjs().subtract(89, 'day').startOf('day').toISOString(),
			to: dayjs().endOf('day').toISOString()
		}
	},
	originCountry: {
		label: 'originCountry',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.COUNTRIES,
			labelPrefix: 'country.'
		},
		filterValues: {
			key: ['originCountry'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	originCity: {
		label: 'originCity',
		type: 'autocomplete',
		autocompleteProps: {
			autoCompleteName: AUTOCOMPLETE.CITIES_LIST,
			selectionLabelAccessor: ['name', 'country'],
			columns: [
				{ accessor: 'name', width: 200 },
				{ accessor: 'country', width: 100 }
			],
			multipleSelection: true
		},
		filterValues: {
			key: ['originCity'],
			action: FilterAction.is,
			filterType: FilterType.multiSelection,
			value: []
		}
	},
	destinationCountry: {
		label: 'destinationCountry',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.COUNTRIES,
			labelPrefix: 'country.',
			multipleSelection: false,
			placeholder: 'selectCountry'
		},
		filterValues: {
			key: ['destinationCountry'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	destinationCity: {
		label: 'destinationCity',
		type: 'autocomplete',
		autocompleteProps: {
			autoCompleteName: AUTOCOMPLETE.CITIES_LIST,
			selectionLabelAccessor: ['name', 'country'],
			columns: [
				{ accessor: 'name', width: 200 },
				{ accessor: 'country', width: 100 }
			],
			multipleSelection: true
		},
		filterValues: {
			key: ['destinationCity'],
			action: FilterAction.is,
			filterType: FilterType.multiSelection,
			value: []
		}
	},
	deliveryCompanyName: {
		label: 'deliveryCompanyName',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.DELIVERY_COMPANY_SETTINGS_FOR_AGGREGATORS_WITH_OMNI,
			multipleSelection: true
		},
		filterValues: {
			key: ['deliveryCompanyName'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	shipmentFlow: {
		label: 'shipmentFlow',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.SHIPMENT_FLOW
		},
		filterValues: {
			key: ['shipmentFlow'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	serviceType: {
		label: 'serviceType',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.PRICE_CONFIGURATION_SERVICE_TYPE,
			multipleSelection: true
		},
		filterValues: {
			key: ['serviceType'],
			action: FilterAction.is,
			filterType: FilterType.multiSelection,
			value: []
		}
	},
	warehouse: {
		label: 'warehouse',
		type: 'autocomplete',
		autocompleteProps: {
			autoCompleteName: AUTOCOMPLETE.WAREHOUSES,
			selectionLabelAccessor: ['Name'],
			columns: [{ accessor: 'Name', width: 200 }],
			multipleSelection: true
		},
		filterValues: {
			key: ['warehouseId'],
			action: FilterAction.is,
			filterType: FilterType.multiSelection,
			value: []
		}
	},
	branch: {
		label: 'branch',
		type: 'autocomplete',
		autocompleteProps: {
			autoCompleteName: AUTOCOMPLETE.BRANCHES,
			selectionLabelAccessor: ['Name'],
			columns: [{ accessor: 'Name', width: 200 }],
			multipleSelection: true
		},
		filterValues: {
			key: ['branchId'],
			action: FilterAction.is,
			filterType: FilterType.multiSelection,
			value: []
		}
	},
	brand: {
		label: 'brand',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.ASSIGNED_BRANDS,
			multipleSelection: true,
			searchable: false
		},
		filterValues: {
			key: ['brandName'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	salesChannel: {
		label: 'salesChannel',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.SALES_CHANNEL_ACTIVE,
			multipleSelection: true
		},
		filterValues: {
			key: ['salesChannel'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	paymentMethod: {
		label: 'paymentMethod',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.PAYMENT_METHOD,
			multipleSelection: true
		},
		filterValues: {
			key: ['paymentMethod'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},
	delayType: {
		label: 'delayType',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.DELAY_TYPE,
			multipleSelection: true
		},
		filterValues: {
			key: ['delayType'],
			action: FilterAction.is,
			filterType: FilterType.enumMulti,
			value: []
		}
	},

	currency: {
		label: 'currency',
		type: 'enum',
		enumProps: {
			enumName: ENUMS.CURRENCY
		},
		filterValues: {
			key: ['currency'],
			action: FilterAction.is,
			filterType: FilterType.enum,
			value: []
		}
	}
};

export const getActiveFilters = (extraFilters) => {
	return extraFilters
		.filter((filter) => {
			return Array.isArray(filter.filterValues?.value) ? filter.filterValues.value.length > 0 : true;
		})
		.map((filter) => filter.filterValues);
};

const CompositeFilter = ({ filtersMenuButtonRef, filtersList, setFiltersList }) => {
	const [filterValues, setFilterValues] = useState(filtersList.map((filter) => filter.filterValues));
	const { userType } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	useEffect(() => {
		setFilterValues(filtersList.map((filter) => filter.filterValues));
	}, [filtersList]);

	const changeFilter = useCallback((keyArray, value) => {
		setFilterValues((prevFilters) =>
			prevFilters.map((filter) =>
				Array.isArray(keyArray) && keyArray.every((key) => filter.key.includes(key))
					? {
							...filter,
							from: value?.from ?? filter.from,
							to: value?.to ?? filter.to,
							value: Array.isArray(filter.value) ? [...(Array.isArray(value) ? value : [value])] : value
						}
					: filter
			)
		);
	}, []);

	const sendFilters = useCallback(() => {
		setFiltersList((prevFiltersList) =>
			prevFiltersList.map((filter) => {
				const updatedFilter = filterValues.find((f) =>
					f.key.every((key) => filter.filterValues.key.includes(key))
				);

				const updatedValue = Array.isArray(updatedFilter?.value)
					? updatedFilter.value.filter((v) => v !== null)
					: updatedFilter?.value;

				if (filter.label === 'shipmentCreatedDate') {
					return {
						...filter,
						filterValues: {
							...filter.filterValues,
							from: updatedFilter?.from ?? filter.filterValues.from,
							to: updatedFilter?.to ?? filter.filterValues.to
						}
					};
				}

				return {
					...filter,
					filterValues: {
						...filter.filterValues,
						value: updatedValue ? updatedValue : filter.filterValues.value
					}
				};
			})
		);
	}, [filterValues, setFiltersList]);

	const filterComponents = useCallback(
		(filterComponent) => {
			const filterValue = filterValues.find((f) =>
				f.key.every((key) => filterComponent.filterValues.key.includes(key))
			);

			switch (filterComponent.type) {
				case 'date':
					return (
						<DateInput
							value={{ start: filterValue?.from, end: filterValue?.to }}
							setValue={(date) => {
								changeFilter(filterComponent.filterValues.key, { from: date?.start, to: date?.end });
							}}
							{...filterComponent.dateProps}
						/>
					);
				case 'autocomplete':
					return (
						<AutoComplete
							value={filterValue.value}
							setValue={(value) => {
								changeFilter(
									filterComponent.filterValues.key,
									value?.map((item) => item.ID)
								);
							}}
							{...filterComponent.autocompleteProps}
						/>
					);
				case 'enum': {
					return (
						<EnumSelect
							value={
								Array.isArray(filterValue?.value) && filterValue.value.length === 0
									? null
									: filterValue?.value
							}
							setValue={(value) => {
								if (
									filterComponent.label === 'brand' &&
									(Array.isArray(value) ? value.length === 0 : !value) &&
									isBrandManager
								) {
									return;
								}
								changeFilter(
									filterComponent.filterValues.key,
									Array.isArray(value) ? value.filter((v) => v !== null) : [value]
								);
							}}
							{...filterComponent.enumProps}
						/>
					);
				}
				default:
					return null;
			}
		},
		[filterValues, changeFilter]
	);

	const renderedFilters = useMemo(
		() =>
			filtersList?.map((filter, index) => (
				<PopperMenuItem key={index}>
					<Typography variant="p2" fontWeight="500">
						{filter.label}
					</Typography>
					{filterComponents(filter)}
				</PopperMenuItem>
			)),
		[filtersList, filterComponents]
	);

	return (
		<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
			{(closePopper) => (
				<PopperMenu>
					{renderedFilters}
					<S.StyledButton
						fullWidth
						onClick={() => {
							sendFilters();
							closePopper();
						}}
						label="applyFilter"
						style={{ flexShrink: '0' }}
					/>
				</PopperMenu>
			)}
		</Popper>
	);
};

export default CompositeFilter;
